import { create } from 'zustand';
import type { Notification, NotificationPreferences } from '@/types';
import { notificationApi } from '@/api';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences;
  isLoading: boolean;
  fetchNotifications: () => Promise<void>;
  markRead: (ids: string[]) => Promise<void>;
  markAllRead: () => void;
  addNotification: (notification: Notification) => void;
  fetchPreferences: () => Promise<void>;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => Promise<void>;
}

const defaultPreferences: NotificationPreferences = {
  in_app_enabled: true,
  wechat_enabled: true,
  sms_enabled: false,
  email_enabled: false,
  push_enabled: true,
};

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  preferences: defaultPreferences,
  isLoading: false,

  fetchNotifications: async () => {
    set({ isLoading: true });
    try {
      const notifications = await notificationApi.getList();
      set({
        notifications,
        unreadCount: notifications.filter((n) => !n.is_read).length,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  markRead: async (ids) => {
    await notificationApi.markRead(ids);
    const { notifications } = get();
    const updated = notifications.map((n) =>
      ids.includes(n.notification_id) ? { ...n, is_read: true } : n,
    );
    set({
      notifications: updated,
      unreadCount: updated.filter((n) => !n.is_read).length,
    });
  },

  markAllRead: () => {
    const { notifications } = get();
    set({
      notifications: notifications.map((n) => ({ ...n, is_read: true })),
      unreadCount: 0,
    });
  },

  addNotification: (notification) => set((state) => ({
    notifications: [notification, ...state.notifications],
    unreadCount: state.unreadCount + (notification.is_read ? 0 : 1),
  })),

  fetchPreferences: async () => {
    try {
      const prefs = await notificationApi.getList();
      set({ preferences: prefs as unknown as NotificationPreferences });
    } catch {
      // use defaults
    }
  },

  updatePreferences: async (prefs) => {
    const updated = { ...get().preferences, ...prefs };
    await notificationApi.updatePreferences(updated);
    set({ preferences: updated });
  },
}));
